/**
 * Runs one page tour with driver.js. Steps whose target is not on the screen are left out.
 * When the tour ends, `onEnd` gets 'done' (reached the last step) or 'skipped' (closed early), so the
 * caller can save it server-side.
 */
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { findTarget, type Tour } from './tours.js';

export type TourEnd = 'done' | 'skipped';

/** The steps that can be shown right now (pure; exported for tests). */
export function visibleSteps(tour: Tour, root: ParentNode = document): DriveStep[] {
  const out: DriveStep[] = [];
  for (const s of tour.steps) {
    const popover = { title: s.title, description: s.text };
    if (!s.target) { out.push({ popover }); continue; }
    const el = findTarget(s.target, root);
    if (el) out.push({ element: el, popover });
  }
  return out;
}

export function runTour(tour: Tour, onEnd: (how: TourEnd) => void): void {
  const steps = visibleSteps(tour);
  // Driver.js reads the element on each step; log what was left out so a missing target is never silent.
  const missing = tour.steps.filter((s) => s.target && !findTarget(s.target)).map((s) => s.target);
  if (missing.length) console.info(`tour ${tour.key}: not on screen now, left out: ${missing.join(', ')}`);
  let ended = false;
  const finish = (how: TourEnd) => { if (!ended) { ended = true; onEnd(how); } };
  // Nothing to point at: end at once so the caller is not left thinking a tour is running.
  if (steps.length === 0) { finish('skipped'); return; }
  const d = driver({
    showProgress: true,
    allowClose: true,
    overlayOpacity: 0.55,
    smoothScroll: true,
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Finish',
    progressText: 'Step {{current}} of {{total}}',
    popoverClass: 'bs-tour',
    steps,
    onNextClick: (_el, _step, opts) => {
      if (!opts.driver.hasNextStep()) { finish('done'); opts.driver.destroy(); return; }
      opts.driver.moveNext();
    },
    // Esc, the x and an overlay click all come here first. driver.js only calls onDestroyed when it
    // still has an active step, so relying on that alone lost some closes (BS-069); record it here.
    onDestroyStarted: (_el, _step, opts) => { finish('skipped'); opts.driver.destroy(); },
    onDestroyed: () => finish('skipped'),
  });
  d.drive();
}
